import { createContext, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { PageHeader, TabBar } from "@fox1ck-org/ui";

import { useT, type TKey } from "@/lib/i18n";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { carryPeriod, tabFromPath } from "@/lib/route-tab";
import { useSearchClicks } from "@/lib/services/click-service";
import { useSearchConversions } from "@/lib/services/conversion-service";
import { useListPostbacks } from "@/lib/services/postback-service";

const ActionsSlot = createContext<HTMLElement | null>(null);

/** A tab's own controls, drawn at the right of the «Журнал» title. */
export function JournalActions({ children }: { children: ReactNode }) {
  const target = useContext(ActionsSlot);
  return target ? createPortal(children, target) : null;
}

/** How often the open journal looks for new rows (доска: «в реальном времени»). */
export const JOURNAL_POLL_MS = 15_000;

export const JOURNAL_TABS = ["clicks", "conversions", "postbacks", "bots"] as const;
export type JournalTab = (typeof JOURNAL_TABS)[number];

const TAB_LABEL: Record<JournalTab, TKey> = {
  clicks: "journal.tab.clicks",
  conversions: "journal.tab.conversions",
  postbacks: "journal.tab.postbacks",
  bots: "journal.tab.bots",
};

/**
 * «Журнал» — the rows behind the numbers (доски JClicks, JConversions,
 * JPostbacks, JBots): one title, four tabs that are addresses
 * (/journal/clicks|conversions|postbacks|bots, decision 04), each with how
 * many rows it holds over the Top bar's period — people, conversions, every
 * partner postback, bots — and the tab below. Switching a tab carries the
 * period and drops the other tab's filters.
 */
export function JournalShell() {
  const t = useT();
  const params = useReportParams();
  const range = { from: params.from, to: params.to, timezone: params.tz };
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // Every tab has its own segment, the first one too: /journal itself is a redirect.
  const tab = tabFromPath(pathname, "/journal", JOURNAL_TABS, "clicks");
  const setTab = (next: JournalTab) => navigate(`/journal/${next}${carryPeriod(search)}`);

  const people = useSearchClicks({ range, trafficClass: "human", limit: 1 }, { refetchInterval: JOURNAL_POLL_MS });
  const convs = useSearchConversions({ range, limit: 1 }, { refetchInterval: JOURNAL_POLL_MS });
  const pbs = useListPostbacks({ from: params.from, to: params.to, timezone: params.tz, limit: 1, offset: 0 }, { refetchInterval: JOURNAL_POLL_MS });
  const bots = useSearchClicks({ range, botsOnly: true, limit: 1 }, { refetchInterval: JOURNAL_POLL_MS });
  const count: Record<JournalTab, number | undefined> = {
    clicks: people.data?.total,
    conversions: convs.data?.total,
    postbacks: pbs.data?.all,
    bots: bots.data?.total,
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-5 pt-4">
        <PageHeader
          title={t("journal.title")}
          actions={<div ref={setSlot} className="flex items-center gap-3" />}
          tabs={
            <TabBar
              aria-label={t("journal.title")}
              value={tab}
              onValueChange={(v) => setTab(v as JournalTab)}
              tabs={JOURNAL_TABS.map((k) => ({ value: k, label: t(TAB_LABEL[k]), count: count[k], panelId: "journal-tab" }))}
            />
          }
        />
      </div>
      <ActionsSlot.Provider value={slot}>
        <div id="journal-tab" role="tabpanel" className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </div>
      </ActionsSlot.Provider>
    </div>
  );
}
